// src/components/ledger/ClientLedger.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { generateInvoice, generateStatement } from './InvoiceGenerator';
import OrderProductionTracker from './OrderProductionTracker';

export const ClientLedger = ({ client, onRefresh, onShowPaymentModal }) => {
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [showProductionTracker, setShowProductionTracker] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (client && client.id) {
      console.log('ClientLedger: Fetching data for client:', client.id);
      fetchClientData();
    }
  }, [client, dateRange]);

  const fetchClientData = async () => {
    console.log('fetchClientData started');
    setLoading(true);
    setError(null);
    try {
      // Fetch orders
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('clientId', '==', client.id),
        orderBy('orderDate', 'desc')
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      console.log('Orders found:', ordersSnapshot.docs.length);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply date filter
      let filteredOrders = ordersData;
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate;
        switch (dateRange) {
          case 'week':
            startDate = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            startDate = new Date(now.setMonth(now.getMonth() - 1));
            break;
          case 'year':
            startDate = new Date(now.setFullYear(now.getFullYear() - 1));
            break;
          default:
            startDate = null;
        }
        if (startDate) {
          filteredOrders = ordersData.filter(order => {
            const orderDate = order.orderDate?.toDate();
            return orderDate && orderDate >= startDate;
          });
        }
      }

      // Fetch payments
      const paymentsRef = collection(db, 'payments');
      const paymentsQuery = query(
        paymentsRef,
        where('clientId', '==', client.id),
        orderBy('paymentDate', 'desc')
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      console.log('Payments found:', paymentsSnapshot.docs.length);
      const paymentsData = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch order items for each order
      const itemsMap = {};
      for (const order of filteredOrders) {
        const itemsRef = collection(db, 'orderItems');
        const itemsQuery = query(itemsRef, where('orderId', '==', order.id));
        const itemsSnapshot = await getDocs(itemsQuery);
        itemsMap[order.id] = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      setOrders(filteredOrders);
      setPayments(paymentsData);
      setOrderItems(itemsMap);
      console.log('Data loaded successfully');
    } catch (error) {
      console.error('Error fetching client data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      console.log('Loading set to false');
    }
  };

  const calculateTotals = () => {
    const totalOrders = orders.reduce((sum, order) => sum + (order.totalSellingPrice || 0), 0);
    const totalPayments = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const balance = totalOrders - totalPayments;
    return { totalOrders, totalPayments, balance };
  };

  const getOrderPayments = (orderId) => {
    return payments.filter(p => p.orderId === orderId).reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  const handleInvoice = async (order) => {
    const items = orderItems[order.id] || [];
    const orderPayments = payments.filter(p => p.orderId === order.id);
    await generateInvoice(order, client, items, orderPayments);
  };

  const handleStatement = async () => {
    await generateStatement(client, orders, payments);
  };

  const { totalOrders, totalPayments, balance } = calculateTotals();

  if (loading) {
    return React.createElement(
      'div',
      { style: localStyles.loadingContainer },
      React.createElement('div', { style: localStyles.spinner }),
      React.createElement('p', { style: localStyles.loadingText }, 'Loading ledger data...')
    );
  }

  if (error) {
    return React.createElement(
      'div',
      { style: localStyles.errorContainer },
      React.createElement('p', { style: localStyles.errorText }, 'Error: ', error),
      React.createElement(
        'button',
        { style: localStyles.retryButton, onClick: fetchClientData },
        'Retry'
      )
    );
  }

  return React.createElement(
    'div',
    { style: localStyles.container },
    // Client Info Header Card
    React.createElement(
      'div',
      { style: localStyles.clientInfoCard },
      React.createElement('div', { style: localStyles.clientInfoAvatar }, '👤'),
      React.createElement(
        'div',
        { style: localStyles.clientInfoDetails },
        React.createElement('h2', { style: localStyles.clientInfoName }, client?.name || 'Unknown Client'),
        React.createElement('div', { style: localStyles.clientInfoContact },
          client?.phone && React.createElement('span', { style: localStyles.clientInfoPhone }, '📞 ', client.phone),
          client?.email && React.createElement('span', { style: localStyles.clientInfoEmail }, '✉️ ', client.email)
        ),
        client?.address && React.createElement('div', { style: localStyles.clientInfoAddress }, '📍 ', client.address)
      )
    ),
    // Summary Cards
    React.createElement(
      'div',
      { style: localStyles.summaryGrid },
      React.createElement(
        'div',
        { style: localStyles.summaryCard },
        React.createElement('div', { style: localStyles.summaryIcon }, '📦'),
        React.createElement(
          'div',
          null,
          React.createElement('div', { style: localStyles.summaryLabel }, 'Total Orders'),
          React.createElement('div', { style: localStyles.summaryValue }, `${totalOrders.toLocaleString()} UGX`)
        )
      ),
      React.createElement(
        'div',
        { style: localStyles.summaryCard },
        React.createElement('div', { style: localStyles.summaryIcon }, '💰'),
        React.createElement(
          'div',
          null,
          React.createElement('div', { style: localStyles.summaryLabel }, 'Total Payments'),
          React.createElement('div', { style: localStyles.summaryValue }, `${totalPayments.toLocaleString()} UGX`)
        )
      ),
      React.createElement(
        'div',
        { style: { ...localStyles.summaryCard, ...(balance > 0 ? localStyles.summaryWarning : balance === 0 ? localStyles.summarySuccess : {}) } },
        React.createElement('div', { style: localStyles.summaryIcon }, '⚖️'),
        React.createElement(
          'div',
          null,
          React.createElement('div', { style: localStyles.summaryLabel }, 'Balance Due'),
          React.createElement('div', { style: localStyles.summaryValue }, `${balance.toLocaleString()} UGX`),
          React.createElement('div', { style: localStyles.summaryStatus },
            balance > 0 ? '⚠️ Pending Payment' : balance === 0 ? '✓ Fully Paid' : '↻ Overpaid'
          )
        )
      )
    ),
    // Filters
    React.createElement(
      'div',
      { style: localStyles.filtersBar },
      React.createElement(
        'div',
        { style: localStyles.filterGroup },
        React.createElement('label', { style: localStyles.filterLabel }, 'Date Range'),
        React.createElement(
          'select',
          { style: localStyles.filterSelect, value: dateRange, onChange: (e) => setDateRange(e.target.value) },
          React.createElement('option', { value: 'all' }, 'All Time'),
          React.createElement('option', { value: 'week' }, 'Last 7 Days'),
          React.createElement('option', { value: 'month' }, 'Last 30 Days'),
          React.createElement('option', { value: 'year' }, 'Last 12 Months')
        )
      ),
      React.createElement(
        'button',
        { style: localStyles.statementButton, onClick: handleStatement },
        '📄 Download Statement'
      )
    ),
    // Orders Table
    React.createElement(
      'div',
      { style: localStyles.section },
      React.createElement(
        'div',
        { style: localStyles.sectionHeader },
        React.createElement('h3', { style: localStyles.sectionTitle }, 'Order History')
      ),
      React.createElement(
        'div',
        { style: localStyles.tableWrapper },
        React.createElement(
          'table',
          { style: localStyles.table },
          React.createElement(
            'thead',
            null,
            React.createElement(
              'tr',
              { style: localStyles.tableHeaderRow },
              React.createElement('th', { style: localStyles.tableHeader }, 'Order #'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Date'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Client'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Phone'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Items'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Total'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Paid'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Balance'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Status'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Production'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Actions')
            )
          ),
          React.createElement(
            'tbody',
            null,
            orders.map(order => {
              const paid = getOrderPayments(order.id);
              const orderBalance = order.totalSellingPrice - paid;
              const items = orderItems[order.id] || [];
              
              return React.createElement(
                React.Fragment,
                { key: order.id },
                React.createElement(
                  'tr',
                  { 
                    style: localStyles.tableRow, 
                    onClick: () => setExpandedOrder(expandedOrder === order.id ? null : order.id) 
                  },
                  React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.orderNumber } }, order.orderNumber),
                  React.createElement('td', { style: localStyles.tableCell }, new Date(order.orderDate?.toDate()).toLocaleDateString()),
                  React.createElement('td', { style: localStyles.tableCell }, React.createElement('strong', null, client?.name || 'N/A')),
                  React.createElement('td', { style: localStyles.tableCell }, client?.phone || 'N/A'),
                  React.createElement(
                    'td',
                    { style: localStyles.tableCell },
                    React.createElement(
                      'div',
                      { style: localStyles.itemsSummary },
                      items.map((item, idx) => React.createElement('span', { key: idx, style: localStyles.itemTag }, `${item.productName} x${item.quantity}`))
                    )
                  ),
                  React.createElement('td', { style: localStyles.tableCell }, order.totalSellingPrice?.toLocaleString()),
                  React.createElement('td', { style: localStyles.tableCell }, paid.toLocaleString()),
                  React.createElement('td', { style: { ...localStyles.tableCell, ...(orderBalance > 0 ? localStyles.balanceDue : localStyles.balanceZero) } }, orderBalance.toLocaleString()),
                  React.createElement(
                    'td',
                    { style: localStyles.tableCell },
                    React.createElement('span', { style: { ...localStyles.statusBadge, ...localStyles[`status${order.status}`] } }, order.status)
                  ),
                  React.createElement(
                    'td',
                    { style: localStyles.tableCell },
                    React.createElement(
                      'button',
                      {
                        style: localStyles.trackerButton,
                        onClick: (e) => {
                          e.stopPropagation();
                          setShowProductionTracker(order.id);
                        },
                        title: 'Track Production Costs'
                      },
                      '🏭 Track'
                    )
                  ),
                  React.createElement(
                    'td',
                    { style: localStyles.tableCell },
                    React.createElement(
                      'div',
                      { style: localStyles.actionButtons },
                      React.createElement(
                        'button',
                        { style: localStyles.iconButton, onClick: (e) => { e.stopPropagation(); handleInvoice(order); }, title: 'Download Invoice' },
                        '📄'
                      ),
                      orderBalance > 0 && React.createElement(
                        'button',
                        { style: localStyles.iconButton, onClick: (e) => { e.stopPropagation(); onShowPaymentModal(); }, title: 'Record Payment' },
                        '💰'
                      ),
                      React.createElement(
                        'button',
                        { style: localStyles.expandButton, onClick: (e) => { e.stopPropagation(); setExpandedOrder(expandedOrder === order.id ? null : order.id); } },
                        expandedOrder === order.id ? '▲' : '▼'
                      )
                    )
                  )
                ),
                expandedOrder === order.id && React.createElement(
                  'tr',
                  null,
                  React.createElement(
                    'td',
                    { colSpan: '11', style: localStyles.expandedCell },
                    React.createElement(
                      'div',
                      { style: localStyles.expandedContent },
                      React.createElement('h4', { style: localStyles.expandedTitle }, 'Order Details'),
                      React.createElement(
                        'table',
                        { style: localStyles.detailTable },
                        React.createElement(
                          'thead',
                          null,
                          React.createElement(
                            'tr',
                            null,
                            React.createElement('th', { style: localStyles.detailHeader }, 'Product'),
                            React.createElement('th', { style: localStyles.detailHeader }, 'Model'),
                            React.createElement('th', { style: localStyles.detailHeader }, 'Quantity'),
                            React.createElement('th', { style: localStyles.detailHeader }, 'Unit Price'),
                            React.createElement('th', { style: localStyles.detailHeader }, 'Total')
                          )
                        ),
                        React.createElement(
                          'tbody',
                          null,
                          items.map((item, idx) => React.createElement(
                            'tr',
                            { key: idx },
                            React.createElement('td', { style: localStyles.detailCell }, item.productName),
                            React.createElement('td', { style: localStyles.detailCell }, item.modelNumber || '-'),
                            React.createElement('td', { style: localStyles.detailCell }, item.quantity),
                            React.createElement('td', { style: localStyles.detailCell }, item.unitSellingPrice?.toLocaleString()),
                            React.createElement('td', { style: localStyles.detailCell }, item.totalSellingPrice?.toLocaleString())
                          ))
                        )
                      )
                    )
                  )
                )
              );
            }),
            orders.length === 0 && React.createElement(
              'tr',
              null,
              React.createElement('td', { colSpan: '11', style: localStyles.emptyState }, 'No orders found for this client')
            )
          )
        )
      )
    ),
    // Payments Table
    React.createElement(
      'div',
      { style: localStyles.section },
      React.createElement(
        'div',
        { style: localStyles.sectionHeader },
        React.createElement('h3', { style: localStyles.sectionTitle }, 'Payment History')
      ),
      React.createElement(
        'div',
        { style: localStyles.tableWrapper },
        React.createElement(
          'table',
          { style: localStyles.table },
          React.createElement(
            'thead',
            null,
            React.createElement(
              'tr',
              { style: localStyles.tableHeaderRow },
              React.createElement('th', { style: localStyles.tableHeader }, 'Date'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Order #'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Client'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Phone'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Amount'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Method'),
              React.createElement('th', { style: localStyles.tableHeader }, 'Reference')
            )
          ),
          React.createElement(
            'tbody',
            null,
            payments.map(payment => {
              const order = orders.find(o => o.id === payment.orderId);
              return React.createElement(
                'tr',
                { key: payment.id, style: localStyles.tableRow },
                React.createElement('td', { style: localStyles.tableCell }, new Date(payment.paymentDate?.toDate()).toLocaleDateString()),
                React.createElement('td', { style: localStyles.tableCell }, order?.orderNumber || 'N/A'),
                React.createElement('td', { style: localStyles.tableCell }, React.createElement('strong', null, client?.name || 'N/A')),
                React.createElement('td', { style: localStyles.tableCell }, client?.phone || 'N/A'),
                React.createElement('td', { style: { ...localStyles.tableCell, ...localStyles.paymentAmount } }, `${payment.amount.toLocaleString()} UGX`),
                React.createElement(
                  'td',
                  { style: localStyles.tableCell },
                  React.createElement('span', { style: { ...localStyles.methodBadge, ...localStyles[`method${payment.paymentMethod}`] } }, payment.paymentMethod)
                ),
                React.createElement('td', { style: localStyles.tableCell }, payment.reference || '-')
              );
            }),
            payments.length === 0 && React.createElement(
              'tr',
              null,
              React.createElement('td', { colSpan: '7', style: localStyles.emptyState }, 'No payments recorded yet')
            )
          )
        )
      )
    ),
    // Production Tracker Modal
    showProductionTracker && React.createElement(OrderProductionTracker, {
      orderId: showProductionTracker,
      orderNumber: orders.find(o => o.id === showProductionTracker)?.orderNumber,
      onClose: () => setShowProductionTracker(null),
      onRefresh: () => {
        fetchClientData();
        if (onRefresh) onRefresh();
      }
    })
  );
};

// Styles
const localStyles = {
  container: {
    padding: '28px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#64748b'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#ef4444'
  },
  errorText: {
    fontSize: '14px',
    marginBottom: '16px'
  },
  retryButton: {
    padding: '8px 16px',
    background: '#7f1a2e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#7f1a2e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  loadingText: {
    fontSize: '14px'
  },
  clientInfoCard: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '20px 24px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    border: '1px solid #eef2f6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
  },
  clientInfoAvatar: {
    fontSize: '48px',
    background: '#f1f5f9',
    width: '70px',
    height: '70px',
    borderRadius: '35px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  clientInfoDetails: {
    flex: 1
  },
  clientInfoName: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '6px'
  },
  clientInfoContact: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '4px'
  },
  clientInfoPhone: {
    fontSize: '13px',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  clientInfoEmail: {
    fontSize: '13px',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  clientInfoAddress: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '28px'
  },
  summaryCard: {
    background: '#ffffff',
    padding: '20px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    border: '1px solid #eef2f6',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
  },
  summaryIcon: {
    fontSize: '32px'
  },
  summaryLabel: {
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#64748b',
    marginBottom: '4px'
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#0f172a'
  },
  summaryWarning: {
    borderLeft: '3px solid #f59e0b'
  },
  summarySuccess: {
    borderLeft: '3px solid #10b981'
  },
  summaryStatus: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '4px'
  },
  filtersBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569'
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  statementButton: {
    padding: '8px 16px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  section: {
    marginBottom: '32px'
  },
  sectionHeader: {
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b'
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
    border: '1px solid #eef2f6'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  tableHeaderRow: {
    background: '#f8fafc',
    borderBottom: '1px solid #eef2f6'
  },
  tableHeader: {
    textAlign: 'left',
    padding: '14px 16px',
    fontWeight: '600',
    color: '#475569'
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background 0.2s ease'
  },
  tableCell: {
    padding: '14px 16px',
    color: '#334155'
  },
  orderNumber: {
    fontWeight: '600',
    color: '#7f1a2e'
  },
  itemsSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  itemTag: {
    background: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#475569'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500'
  },
  statusdeposit: {
    background: '#fef3c7',
    color: '#d97706'
  },
  statusproduction: {
    background: '#dbeafe',
    color: '#2563eb'
  },
  statuscompleted: {
    background: '#d1fae5',
    color: '#059669'
  },
  statusdelivered: {
    background: '#e0e7ff',
    color: '#4f46e5'
  },
  balanceDue: {
    color: '#f59e0b',
    fontWeight: '600'
  },
  balanceZero: {
    color: '#10b981',
    fontWeight: '600'
  },
  paymentAmount: {
    fontWeight: '600',
    color: '#10b981'
  },
  methodBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500'
  },
  methodcash: {
    background: '#f1f5f9',
    color: '#475569'
  },
  methodbank: {
    background: '#dbeafe',
    color: '#2563eb'
  },
  methodmobile: {
    background: '#fce7f3',
    color: '#db2777'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  trackerButton: {
    background: '#7f1a2e',
    color: '#ffffff',
    padding: '6px 12px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  iconButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    transition: 'transform 0.2s ease'
  },
  expandButton: {
    background: 'none',
    border: 'none',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: '4px'
  },
  expandedCell: {
    padding: '20px',
    background: '#fafcff',
    borderTop: '1px solid #eef2f6'
  },
  expandedContent: {
    padding: '8px'
  },
  expandedTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px'
  },
  detailTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px'
  },
  detailHeader: {
    textAlign: 'left',
    padding: '8px 12px',
    background: '#f8fafc',
    fontWeight: '600',
    color: '#475569'
  },
  detailCell: {
    padding: '8px 12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#475569'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: '#94a3b8',
    fontSize: '13px'
  }
};

// Add keyframes animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default ClientLedger;